import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Globe, Plus, Trash2, RefreshCw, Copy, Check, ShieldCheck, Star, X, AlertTriangle, ChevronRight } from "lucide-react";
import { sr } from "../../lib/sr";

const STATE = {
  not_configured: { label: "Not configured", bg: "#F1F5F9", fg: "#64748B" },
  action_required: { label: "Action required", bg: "#FEE2E2", fg: "#B91C1C" },
  awaiting_ownership: { label: "Awaiting ownership verification", bg: "#FEF3C7", fg: "#92400E" },
  certificate_provisioning: { label: "Certificate provisioning", bg: "#DBEAFE", fg: "#1D4ED8" },
  checking_app: { label: "Checking application connection", bg: "#E0E7FF", fg: "#4338CA" },
  active: { label: "Active", bg: "#DCFCE7", fg: "#166534" },
  misconfigured: { label: "Misconfigured", bg: "#FEE2E2", fg: "#B91C1C" },
  disabled: { label: "Disabled", bg: "#F1F5F9", fg: "#64748B" },
};

function StatePill({ state }) {
  const s = STATE[state] || STATE.not_configured;
  return <span data-testid={`domain-state-${state}`} style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "3px 10px", background: s.bg, color: s.fg }}>{s.label}</span>;
}

function CopyBtn({ text, tid }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(text || ""); setDone(true); setTimeout(() => setDone(false), 1200); };
  return (
    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={copy} data-testid={tid} title="Copy">
      {done ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

function DnsTable({ records }) {
  if (!records?.length) return null;
  const KIND = { routing: "DNS routing", ownership: "Ownership verification", certificate: "Certificate validation" };
  return (
    <div style={{ marginTop: 10 }} data-testid="domain-dns-table">
      {records.map((r, i) => (
        <div key={i} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10, marginBottom: 8, background: "#FBFCFE" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: .3, color: "var(--blue)", textTransform: "uppercase" }}>{KIND[r.kind] || r.kind}</span>
            <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "1px 7px", background: "#EEF2F7", color: "var(--navy)" }}>{r.type}</span>
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 12.5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="sr-muted" style={{ width: 54 }}>Name</span>
              <code style={{ flex: 1, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, padding: "4px 8px", overflowX: "auto", color: "var(--navy)" }}>{r.name}</code>
              <CopyBtn text={r.name} tid={`dns-copy-name-${i}`} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="sr-muted" style={{ width: 54 }}>{r.type === "CNAME" ? "Target" : "Value"}</span>
              <code style={{ flex: 1, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, padding: "4px 8px", overflowX: "auto", color: "var(--navy)" }}>{r.value}</code>
              <CopyBtn text={r.value} tid={`dns-copy-value-${i}`} />
            </div>
          </div>
          <div className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>{r.purpose}</div>
        </div>
      ))}
      <p className="sr-muted" style={{ fontSize: 11 }}>Add these at your domain's current DNS provider. Field names vary slightly between providers.</p>
    </div>
  );
}

function DomainRow({ d, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const act = async (fn, key, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); onChanged(r?.domain); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(""); }
  };
  return (
    <div className="sr-card" style={{ padding: 14 }} data-testid={`domain-${d.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Globe size={18} style={{ color: "var(--blue)" }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong style={{ color: "var(--navy)", fontSize: 15 }} data-testid={`domain-host-${d.id}`}>{d.hostname}</strong>
            {d.is_primary && <span style={{ fontSize: 10, fontWeight: 800, color: "#166534", background: "#DCFCE7", borderRadius: 999, padding: "1px 8px", display: "inline-flex", alignItems: "center", gap: 3 }}><Star size={10} /> Primary</span>}
          </div>
          <div className="sr-muted" style={{ fontSize: 12 }}>{d.scope === "agency" ? "Agency portal" : "Client workspace"}</div>
        </div>
        <StatePill state={d.state} />
      </div>
      {d.last_error && <div style={{ marginTop: 8, fontSize: 12, color: "#B91C1C", display: "flex", gap: 6, alignItems: "center" }} data-testid={`domain-error-${d.id}`}><AlertTriangle size={13} /> {d.last_error}</div>}
      {(d.verification_errors?.length > 0) && <div className="sr-muted" style={{ marginTop: 4, fontSize: 11.5 }}>{d.verification_errors.map((e, i) => <div key={i}>• {typeof e === "string" ? e : (e.message || JSON.stringify(e))}</div>)}</div>}
      {d.app_check && !d.app_check.ok && d.state === "checking_app" && <div className="sr-muted" style={{ marginTop: 6, fontSize: 11.5 }}>{d.app_check.reason}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setOpen((v) => !v)} data-testid={`domain-dns-toggle-${d.id}`}>
          <ChevronRight size={13} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} /> DNS instructions
        </button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => act(() => sr.checkDomain(d.id), "check")} disabled={busy} data-testid={`domain-check-${d.id}`}>
          {busy === "check" ? <Loader2 size={13} className="sr-spin" /> : <RefreshCw size={13} />} Check again
        </button>
        {["awaiting_ownership", "certificate_provisioning", "action_required"].includes(d.state) && (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => act(() => sr.retryDomain(d.id), "retry", "Re-checking validation")} disabled={busy} data-testid={`domain-retry-${d.id}`}>Retry validation</button>
        )}
        {d.state === "active" && !d.is_primary && (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => act(() => sr.setPrimaryDomain(d.id), "primary", "Set as primary")} disabled={busy} data-testid={`domain-primary-${d.id}`}><Star size={13} /> Make primary</button>
        )}
        <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ color: "#B91C1C", marginLeft: "auto" }} onClick={() => { if (window.confirm(`Disconnect ${d.hostname}? This stops serving that domain.`)) act(() => sr.disconnectDomain(d.id), "disc", "Disconnected"); }} disabled={busy} data-testid={`domain-disconnect-${d.id}`}><Trash2 size={13} /> Disconnect</button>
      </div>
      {open && <DnsTable records={d.dns_instructions} />}
    </div>
  );
}

function ConnectForm({ clients, onConnected }) {
  const [scope, setScope] = useState("agency");
  const [hostname, setHostname] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    if (!hostname.trim()) return;
    try { const r = await sr.domainCheckAvailability(hostname); setHostname(r.hostname); setAvail(r.available); }
    catch (e) { setAvail(null); toast.error(e?.response?.data?.detail || "Invalid hostname"); }
  };
  const connect = async () => {
    if (scope === "client" && !tenantId) { toast.error("Pick the client this domain opens"); return; }
    setBusy(true);
    try {
      const r = await sr.claimDomain({ scope, hostname, tenant_id: scope === "client" ? tenantId : undefined });
      toast.success("Domain added — follow the DNS steps to verify it");
      setHostname(""); setAvail(null); onConnected(r.domain);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add domain"); }
    finally { setBusy(false); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="domain-connect-form">
      <strong style={{ color: "var(--navy)" }}>Connect a domain</strong>
      <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
        {[{ k: "agency", l: "Agency portal" }, { k: "client", l: "Client workspace" }].map((o) => (
          <button key={o.k} type="button" onClick={() => setScope(o.k)} data-testid={`domain-scope-${o.k}`}
            style={{ border: "1px solid " + (scope === o.k ? "var(--blue)" : "var(--border)"), background: scope === o.k ? "var(--blue)" : "#fff", color: scope === o.k ? "#fff" : "var(--navy)", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {o.l}
          </button>
        ))}
      </div>
      {scope === "client" && (
        <div className="sr-field" style={{ marginBottom: 8 }}>
          <label className="sr-label">Which client does this domain open?</label>
          <select className="sr-select" value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="domain-client-select">
            <option value="">Select a client…</option>
            {(clients || []).map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <div className="sr-field" style={{ flex: 1 }}>
          <label className="sr-label">Hostname</label>
          <input className="sr-input" value={hostname} onChange={(e) => { setHostname(e.target.value); setAvail(null); }} onBlur={check} placeholder={scope === "agency" ? "app.youragency.com" : "marketing.clientname.com"} data-testid="domain-hostname-input" />
        </div>
        <button className="sr-btn sr-btn-primary" onClick={connect} disabled={busy || !hostname.trim() || avail === false} data-testid="domain-connect-btn">{busy ? <Loader2 size={14} className="sr-spin" /> : <><Plus size={14} /> Connect</>}</button>
      </div>
      {avail === true && <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }} data-testid="domain-available-yes"><Check size={12} /> {hostname} is available to connect</div>}
      {avail === false && <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 6 }} data-testid="domain-available-no"><X size={12} /> That hostname is already connected</div>}
      <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 8 }}>Use an explicit subdomain (e.g. app.youragency.com). Adding a domain never activates it or grants access — you'll verify ownership via DNS next.</p>
    </div>
  );
}

function PathLinkCard({ clients }) {
  const [scope, setScope] = useState("agency");
  const [tenantId, setTenantId] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const gen = async () => {
    if (scope === "client" && !tenantId) { toast.error("Pick the client this link opens"); return; }
    setBusy(true);
    try { const r = await sr.pathLink(scope, scope === "client" ? tenantId : undefined); setLink(origin + r.path); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create link"); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Branded link copied"); };
  return (
    <div className="sr-card" style={{ marginBottom: 16, borderLeft: "3px solid #16A34A", background: "#F0FDF4" }} data-testid="domains-path-link">
      <strong style={{ color: "var(--navy)" }}>Branded link — works today (no DNS or custom domain needed)</strong>
      <p className="sr-muted" style={{ fontSize: 12.5, margin: "4px 0 10px" }}>Share a branded path on this app while custom domains await production hosting. The portal is themed for the workspace; visitors still sign in to their own account.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        {[{ k: "agency", l: "Agency portal" }, { k: "client", l: "Client workspace" }].map((o) => (
          <button key={o.k} type="button" onClick={() => { setScope(o.k); setLink(""); }} data-testid={`path-scope-${o.k}`}
            style={{ border: "1px solid " + (scope === o.k ? "#16A34A" : "var(--border)"), background: scope === o.k ? "#16A34A" : "#fff", color: scope === o.k ? "#fff" : "var(--navy)", borderRadius: 999, padding: "6px 13px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{o.l}</button>
        ))}
      </div>
      {scope === "client" && (
        <select className="sr-select" value={tenantId} onChange={(e) => { setTenantId(e.target.value); setLink(""); }} data-testid="path-client-select" style={{ marginBottom: 10 }}>
          <option value="">Pick a client…</option>
          {(clients || []).map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.name}</option>)}
        </select>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={gen} disabled={busy} data-testid="path-generate-btn">{busy ? <Loader2 size={13} className="sr-spin" /> : <><Globe size={13} /> Get branded link</>}</button>
        {link && <>
          <code style={{ fontSize: 12.5, background: "#fff", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 10px", flex: 1, minWidth: 200, overflowX: "auto" }} data-testid="path-link-value">{link}</code>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={copy} data-testid="path-link-copy"><Copy size={13} /> Copy</button>
        </>}
      </div>
    </div>
  );
}

export default function Domains() {
  const [cfg, setCfg] = useState(null);
  const [domains, setDomains] = useState(null);
  const [clients, setClients] = useState([]);

  const load = () => sr.domains().then((r) => setDomains(r.domains || [])).catch(() => setDomains([]));
  useEffect(() => {
    sr.domainsConfig().then(setCfg).catch(() => setCfg({ configured: false }));
    load();
    sr.agencyOverview().then((r) => setClients(r.clients || [])).catch(() => {});
  }, []);

  const onChanged = () => load();

  return (
    <div style={{ maxWidth: 860 }} data-testid="domains-page">
      <h1 style={{ fontSize: 24, color: "var(--navy)", margin: "0 0 4px" }} data-testid="domains-heading"><Globe size={22} style={{ verticalAlign: "-3px" }} /> White-label Domains</h1>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 16 }}>Let agencies and clients reach their branded SmartReachAI portal at their own domain, secured with HTTPS via Cloudflare.</p>

      {cfg && cfg.hosting_note && (
        <div className="sr-card" style={{ marginBottom: 16, borderLeft: "3px solid #F59E0B", background: "#FFFBEB" }} data-testid="domains-hosting-note">
          <div style={{ display: "flex", gap: 10 }}>
            <AlertTriangle size={18} style={{ color: "#B45309", flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, color: "#92400E" }}>{cfg.hosting_note}</div>
          </div>
        </div>
      )}
      {cfg && !cfg.configured && (
        <div className="sr-card" style={{ marginBottom: 16, borderLeft: "3px solid #B91C1C" }} data-testid="domains-not-configured">
          <strong style={{ color: "var(--navy)" }}>Cloudflare isn't connected yet</strong>
          <p className="sr-muted" style={{ fontSize: 12.5, margin: "4px 0 0" }}>A platform admin must add a Cloudflare API token (scoped to <b>SSL and Certificates → Edit</b> + <b>Zone → Read</b>) and set the fallback origin before domains can be verified.</p>
        </div>
      )}

      <ConnectForm clients={clients} onConnected={onChanged} />

      <PathLinkCard clients={clients} />

      {domains === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        : domains.length === 0 ? <div className="sr-empty" data-testid="domains-empty">No domains connected yet. Add your first branded hostname above.</div>
        : (
          <div style={{ display: "grid", gap: 12 }}>
            {domains.map((d) => <DomainRow key={d.id} d={d} onChanged={onChanged} />)}
          </div>
        )}
    </div>
  );
}
